/**
 * Function to join classes
 * @param args classes to join
 * @returns joined classes
 *
 * @example
 * classes("flex flex-col", isTrue && "items-center") // "flex flex-col items-center"
 */
export function classes(...args) {
    return args.filter(arg => typeof arg === "string").map(e => e.trim()).filter(e => !!e).join(' ');
}
/**
 * Function to join styles
 * @param args styles to join
 * @returns joined styles
 */
export function styles(...args) {
    return args.filter(arg => typeof arg === "string").map(e => e.trim()).join(' ');
}
/**
 * Function to return the given array without null or undefined values
 * @param item array to filter
 * @returns filtered array
 *
 * @example
 * nonNullArray([1, 2, null, 3, undefined, 4]) // [1, 2, 3, 4]
 */
export function nonNullArray(item) {
    return item?.filter(item => !!item) ?? [];
}
/**
 * Function to sleep for the given amount of milliseconds.
 * @example
 * sleep(1000).then(() => console.log("1 second passed"));
 *
 * await sleep(2000);
 * console.log("2 seconds passed");
 */
export async function sleep(ms = 0) {
    return new Promise(resolve => setTimeout(resolve, ms));
}
/**
 * Fetch with timeout.
 * @example
 * timeoutFetch("https://example.com", undefined, 1000).then(response => console.log(response));
 */
export async function timeoutFetch(input, init, timeout = 60000) {
    if (timeout) {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), timeout);
        return fetch(input, { ...init, signal: controller.signal });
    }
    return fetch(input, init);
}
/**
 * Fetch that can be aborted manually.
 * @example
 * const controller = new AbortController();
 * const fetch = abortableFetch(controller);
 * fetch("https://example.com").then(response => console.log(response));
 * controller.abort();
 */
export function abortableFetch(controller) {
    return async (input, init) => {
        if (controller) {
            return fetch(input, { ...init, signal: controller.signal });
        }
        return fetch(input, init);
    };
}
/**
 * Returns the number as formatted string.
 * @param number The number to format.
 * @param separator The separator to use.
 * @returns e.g 100.000
 */
export function getAsSeparatedNumber(number, separator = ".") {
    return number.toFixed().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}
const debouncedFunctions = {};
/**
 * Debounces a function-call identified by it's name
 * @param functionToDebounce The function to debounce
 *
 * @example
 * debounce(() => console.log("Hello World"), 1000);
 */
export async function debounce(functionToDebounce, time = 100) {
    let timer = debouncedFunctions[functionToDebounce.name];
    if (timer) {
        if (typeof timer === "number") {
            clearTimeout(timer);
        }
        else {
            clearTimeout(timer);
        }
    }
    timer = setTimeout(functionToDebounce, time);
    debouncedFunctions[functionToDebounce.name] = timer;
}
;
/**
 * Retries a function call if it fails
 * @param fn the function to call
 * @param retries the number of retries
 * @param timeout the timeout between retries
 */
export async function retry(fn, retries = 3, timeout = 1000) {
    return fn.catch(async (e) => {
        if (retries > 0) {
            return sleep(timeout).then(() => retry(fn, retries - 1));
        }
        throw e;
    });
}
/**
 * Removes duplicate elements from an array.
 *
 * If no comparator is provided, the default comparator is used which checks
 * for reference equality.
 * @param items The array to remove duplicates from.
 * @param comparator A comparator function.
 */
export function deduplicate(items, comparator = (i1, i2) => i1 === i2) {
    return items.reduce((acc, curr) => {
        const item = acc.findIndex((e) => comparator(e, curr));
        if (item === -1) {
            acc.push(curr);
        }
        return acc;
    }, []);
}
/**
 * Triggers the callback when the element is in viewport.
 * @param target The HTMLElement to watch.
 * @param callback The callback to trigger.
 */
export const observeInViewport = (target, callback, threshold = 0) => new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
        if (entry.isIntersecting) {
            callback(true);
        }
        else if (!entry.isIntersecting) {
            callback(false);
        }
    });
}, { threshold }).observe(target);
/**
 * Clamps the value to min and max.
 */
export function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
/**
 * Checks two objects for equality.
 */
export function isEqual(obj1, obj2) {
    if (obj1 === obj2) {
        return true;
    }
    if (obj1 == null || obj2 == null) {
        return false;
    }
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
        return false;
    }
    const obj1Keys = Object.keys(obj1);
    const obj2Keys = Object.keys(obj2);
    if (obj1Keys.length !== obj2Keys.length) {
        return false;
    }
    for (const key of obj1Keys) {
        if (!obj2Keys.includes(key) || !isEqual(obj1[key], obj2[key])) {
            return false;
        }
    }
    return true;
}
